import React, { createContext, useContext, useState, useCallback } from "react";

interface ValidationError {
  fieldId: string;
  message: string;
}

interface ValidationContextType {
  errors: ValidationError[];
  setErrors: (errors: ValidationError[]) => void;
  addError: (error: ValidationError) => void;
  removeError: (fieldId: string) => void;
  clearErrors: () => void;
  showValidation: boolean;
  setShowValidation: (show: boolean) => void;
  hasErrors: (patterns: string[]) => boolean;
  getErrorMessages: (patterns: string[]) => string[];
  validateFields: (
    validations: Array<{
      fieldId: string;
      value: any;
      validate: (value: any) => string | undefined;
    }>,
  ) => boolean;
}

const ValidationContext = createContext<ValidationContextType | undefined>(
  undefined,
);

export const ValidationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  const addError = useCallback((error: ValidationError) => {
    setErrors((prev) => {
      const filtered = prev.filter((e) => e.fieldId !== error.fieldId);
      return [...filtered, error];
    });
  }, []);

  const removeError = useCallback((fieldId: string) => {
    setErrors((prev) => prev.filter((error) => error.fieldId !== fieldId));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const hasErrors = useCallback(
    (patterns: string[]) => {
      return errors.some((error) =>
        patterns.some((pattern) => error.fieldId.startsWith(pattern)),
      );
    },
    [errors],
  );

  const getErrorMessages = useCallback(
    (patterns: string[]) => {
      return errors
        .filter((error) =>
          patterns.some((pattern) => error.fieldId.startsWith(pattern)),
        )
        .map((error) => error.message);
    },
    [errors],
  );

  const validateFields = useCallback(
    (
      validations: Array<{
        fieldId: string;
        value: any;
        validate: (value: any) => string | undefined;
      }>,
    ) => {
      const newErrors: ValidationError[] = [];

      validations.forEach(({ fieldId, value, validate }) => {
        const errorMessage = validate(value);
        if (errorMessage) {
          newErrors.push({ fieldId, message: errorMessage });
        }
      });

      setErrors((prev) => {
        const remainingErrors = prev.filter(
          (error) => !validations.some((v) => v.fieldId === error.fieldId),
        );
        return [...remainingErrors, ...newErrors];
      });

      return newErrors.length === 0;
    },
    [],
  );

  return (
    <ValidationContext.Provider
      value={{
        errors,
        setErrors,
        addError,
        removeError,
        clearErrors,
        showValidation,
        setShowValidation,
        hasErrors,
        getErrorMessages,
        validateFields,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidation = () => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error("useValidation must be used within a ValidationProvider");
  }
  return context;
};
